import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Project } from '@/lib/types/project';

interface DeleteProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project: Project;
  jobCount: number;
  onConfirm: () => Promise<void>;
}

export function DeleteProjectDialog({
  open,
  onOpenChange,
  project,
  jobCount,
  onConfirm,
}: DeleteProjectDialogProps) {
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (confirmText !== project.name) return;

    setDeleting(true);
    try {
      await onConfirm();
      onOpenChange(false);
      setConfirmText('');
    } catch (error) {
      console.error('Error deleting project:', error);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Project</AlertDialogTitle>
          <AlertDialogDescription className="space-y-2">
            {jobCount > 0 ? (
              <>
                <p className="text-destructive font-semibold">
                  This project contains {jobCount} job{jobCount !== 1 ? 's' : ''}.
                </p>
                <p>
                  You cannot delete a project that contains jobs. Please remove all jobs from this
                  project first, or move them to another project.
                </p>
              </>
            ) : (
              <>
                <p>
                  Are you sure you want to delete the project <strong>{project.name}</strong>?
                </p>
                <p>This action cannot be undone.</p>
                <div className="pt-4">
                  <Label htmlFor="confirm-name">
                    Type <strong>{project.name}</strong> to confirm:
                  </Label>
                  <Input
                    id="confirm-name"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="Enter project name"
                    className="mt-2"
                  />
                </div>
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
              setConfirmText('');
            }}
            disabled={deleting}
          >
            Cancel
          </Button>
          {jobCount === 0 && (
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={confirmText !== project.name || deleting}
            >
              {deleting ? 'Deleting...' : 'Delete Project'}
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
